import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@store/auth.store';

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach access token to every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: string) => void; reject: (e: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

// Auto-refresh on 401
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      const { refreshToken, setTokens, logout } = useAuthStore.getState();
      if (!refreshToken) {
        logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        setTokens(accessToken, newRefreshToken);
        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

/* B-556: ile własnych żądań API poszło i nie wróciło. Interceptory zarejestrowane PO odświeżaniu tokena
   (powyżej): odpowiedź przechodzi najpierw przez nie, więc żądanie czekające na rotację refresh-tokena
   nadal się liczy — a przeładowanie karty w środku rotacji to to, które wylogowuje (serwer wydał nowy
   token, strona zginęła, zanim go zapisała). Czyta to `AppUpdateWatcher` przed automatyczną wymianą wersji. */
let inFlight = 0;
apiClient.interceptors.request.use((config) => { inFlight++; return config; });
apiClient.interceptors.response.use(
  (res) => { inFlight = Math.max(0, inFlight - 1); return res; },
  (err) => { inFlight = Math.max(0, inFlight - 1); return Promise.reject(err); },
);
export function apiRequestsInFlight(): number {
  return inFlight;
}

/**
 * Unwrap the standard ApiResponse envelope `{ success, data, timestamp, path }`.
 *
 * ⚠️ Must NOT be `res.data.data ?? res.data`: when an enveloped endpoint legitimately
 * returns `data: null` (a nullable resource that doesn't exist — e.g. „no CCTV request
 * for this event yet", „no current shift"), `null ?? res.data` would hand back the WHOLE
 * envelope object (truthy), so callers doing `if (result)` see a fake object instead of
 * the intended `null`. That footgun silently broke the „Generuj wniosek CCTV" flow.
 * Detect the envelope by shape and return its `.data` (including null); pass through any
 * genuinely non-enveloped body unchanged.
 */
function unwrap<T>(body: any): T {
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return body.data as T;
  }
  return body as T;
}

// Typed helpers that unwrap the ApiResponse envelope
export const api = {
  get: async <T>(url: string, params?: Record<string, any>): Promise<T> => {
    const res = await apiClient.get(url, { params });
    return unwrap<T>(res.data);
  },
  post: async <T>(url: string, data?: any): Promise<T> => {
    const res = await apiClient.post(url, data);
    return unwrap<T>(res.data);
  },
  patch: async <T>(url: string, data?: any): Promise<T> => {
    const res = await apiClient.patch(url, data);
    return unwrap<T>(res.data);
  },
  put: async <T>(url: string, data?: any): Promise<T> => {
    const res = await apiClient.put(url, data);
    return unwrap<T>(res.data);
  },
  delete: async <T>(url: string): Promise<T> => {
    const res = await apiClient.delete(url);
    return unwrap<T>(res.data);
  },
  upload: async <T>(url: string, formData: FormData): Promise<T> => {
    const res = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<T>(res.data);
  },
};
